import { useMemo } from 'react';
import _ from 'lodash';
import ReactApexChart from 'react-apexcharts';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import history from '@history';
import Typography from '@mui/material/Typography';
import clsx from 'clsx';

const defaultXaxis = ['VAL1', 'VAL2', 'VAL3', 'VAL4', 'VAL5', 'VAL6', 'VAL7'];

const types = [
  'line',
  'area',
  'bar',
  'pie',
  'donut',
  'radialBar',
  'scatter',
  'bubble',
  'heatmap',
  'candlestick',
  'boxPlot',
  'radar',
  'polarArea',
  'rangeBar',
  'rangeArea',
  'treemap',
];

const defaultOptions = () => ({
  chart: {
    animations: {
      enabled: false,
    },
    fontFamily: 'inherit',
    foreColor: 'inherit',
    sparkline: {
      enabled: true,
    },
  },
  stroke: {
    curve: 'smooth',
  },
  tooltip: {
    followCursor: true,
    theme: 'dark',
  },
  xaxis: {
    categories: defaultXaxis,
  },
  plotOptions: {
    heatmap: {
      yRatio: 0.75,
    },
  },
});

/**
 * GaugeCart
 *
 * Componente de gráfica tipo gauge/cart, con soporte para etiquetas, chips y enlaces.
 *
 * @component
 * @param {string} [className] - Clases CSS adicionales para el contenedor
 * @param {object} [style] - Estilos adicionales para el contenedor
 * @param {Array<object>} [data] - Serie de datos para la gráfica
 * @param options
 * @param {Array<string>} [xPoints] - Etiquetas del eje X
 * @param {string} [label] - Título de la gráfica
 * @param {string|React.ReactNode} [helper] - Texto auxiliar destacado
 * @param {{ label: string, color?: string, variant?: string, path?: string }} [link] - Objeto de enlace para mostrar un chip de acción
 * @param {string} [chipLabel] - Etiqueta adicional en chip
 * @param {string} [type='area'] - Tipo de gráfica (area, line, etc.)
 * @returns {JSX.Element}
 */
export const ApexWrapper = ({
  className,
  style,
  series,
  options,
  label,
  helper,
  link,
  chipLabel,
  type = 'area',
}) => {
  const finalType = useMemo(() => {
    if (!types.includes(type)) throw new Error(`Unknown type '${type}' for Apex wrapper`);
    return type;
  }, [type, types]);

  const finalOptions = useMemo(() => {
    if (!options || _.isEmpty(options) || !_.isObject(options)) return defaultOptions();
    return options;
  }, [options]);

  const handleRedirect = (path) => {
    if (path === undefined) return;
    history.push(path);
  };

  return (
    <Paper
      sx={style}
      className={clsx(
        'flex flex-col shadow rounded-2xl overflow-hidden min-h-[240px] max-h-[600px]',
        className
      )}
    >
      <div className="h-[24px] flex flex-row items-start justify-between mx-24 mt-14 mb-0">
        {label !== undefined && (
          <Typography className="text-lg font-medium tracking-tight leading-6 truncate">
            {label}
          </Typography>
        )}
        <div className="flex flex-col justify-start items-end">
          {link !== undefined && (
            <Chip
              size="small"
              color={link?.color}
              variant={link.variant || 'filled'}
              disabled={link.path === undefined}
              onClick={() => handleRedirect(link?.path)}
              label={link.label || 'Ver más'}
            />
          )}
          {chipLabel !== undefined && (
            <div className="mt-8">
              <Chip size="small" className="font-medium text-sm" label={chipLabel} />
            </div>
          )}
        </div>
      </div>
      <div className="h-[60px] flex flex-col lg:flex-row lg:items-center mx-24">
        {helper && (
          <Typography className="text-7xl font-bold tracking-tighter leading-tight">
            {helper}
          </Typography>
        )}
      </div>
      <div className="min-h-[80px] h-full w-full flex flex-auto flex-col">
        {Array.isArray(series) && (
          <ReactApexChart
            className="p-0 m-0"
            options={finalOptions}
            series={series}
            type={finalType}
            height="100%"
          />
        )}
      </div>
    </Paper>
  );
};
